import { LoadingSkeleton, CustomButton } from '@/shared/ui/components';
import { addShipmentIcon, emptyIcon } from '@/shared/ui/assets';
import { auth } from '@/shared/lib';
import classNames from 'classnames';
import _ from 'lodash';
import { t } from 'ttag';
import { checkIcorDevice } from '@/entities/device';
import { isManagedC1cDevice } from '@/entities/study';
import NotAvailableComponent from '../notAvailableComponent/notAvailableComponent';
import { useReturnStatusTab } from './useReturnStatusTab';
import LabelInformation from './labelInformation';
import ReturnStatusCheckbox from './returnStatusCheckbox';
import ReturnStatusNotes from './returnStatusNotes';
import type { ReturnStatusTabProps } from './returnStatusTab.types';
import './returnStatusTab.scss';

const ReturnStatusTab = ({
  isLoadedData: isLoadedDataProp,
  data,
  updateStudyDetail,
  countries,
  hasLoginBanner,
  handleRefreshTracking,
  isC1cProviderStudy = false,
}: ReturnStatusTabProps) => {
  const { state, isLoadedData, onClickCreateShipping, onClickViewNow } = useReturnStatusTab({
    isLoadedData: isLoadedDataProp,
    data,
  });

  const isEmptyTrackingInfo = _.isEmpty(data?.patientReturn?.returnLabels);

  /**
   * Whether the currently viewed study is the follow-on study of a linked pair. True when linkedStudies is non-empty and the study's own id equals the id of the second linked study.
   *
   * @returns True when this study is the follow-on of its linked studies.
   */
  const isFollowOnStudy =
    data?.linkedStudies?.length > 0 && data?.id === data?.linkedStudies?.[1]?.id;

  /**
   * The small outline button that starts creation of a return shipping label. Bound to onClickCreateShipping and reused both in the empty tracking placeholder and elsewhere in the tab.
   *
   * @returns Create-return-shipping button element.
   */
  const createReturnShippingButton = (
    <CustomButton
      id="create-return-shipping-btn"
      type="outline"
      onClick={onClickCreateShipping}
      size="small"
    >
      {t`Create return shipping label`}
    </CustomButton>
  );

  /**
   * Renders the tracking information block of the study return status tab. With no tracking info it shows an empty-state icon, a 'No shipping label created yet' message and, once data has loaded, the create-return-shipping button; otherwise it maps every return label to a label information panel wired with the transaction, shipment date, rate, tracking history, countries and US/CA state lists, and a refresh handler tagged with the ReturnStatusTab type.
   *
   * @returns The tracking information panels or the empty state.
   */
  const renderTrackingInfor = () => {
    if (isEmptyTrackingInfo) {
      return (
        <div className="tracking-infor__empty">
          <img src={addShipmentIcon} alt="empty icon" />
          <div className="description">{t`No shipping label created yet`}</div>
          {isLoadedData && createReturnShippingButton}
        </div>
      );
    }
    return (
      <div className="tracking-infor__wrap">
        {_.map(data?.patientReturn?.returnLabels, (item, index) => (
          <LabelInformation
            key={item?.id}
            index={index}
            transaction={item?.transaction}
            shipmentDate={item?.shipmentDate}
            timezone={data?.timezone}
            id={data?.patientReturn?.id}
            studyFid={data?.friendlyId}
            rate={item?.rate}
            trackingHistory={item?.tracking?.trackingHistory}
            country={countries}
            stateUS={state.listStatesUS}
            stateCA={state.listStatesCA}
            handleRefreshTracking={({ transactionId, ticketId, index, callback }) => {
              handleRefreshTracking({
                type: 'ReturnStatusTab',
                transactionId,
                ticketId,
                index,
                callback,
              });
            }}
          />
        ))}
      </div>
    );
  };

  if (isManagedC1cDevice(isC1cProviderStudy, data?.deviceType)) {
    return (
      <NotAvailableComponent
        containerClassName="return-status-tab"
        message={t`Return status is not available for external studies.`}
        hasLoginBanner={hasLoginBanner}
      />
    );
  }

  if (isFollowOnStudy) {
    return (
      <div
        className={classNames('return-status-tab__follow-on', {
          'has-marketing-banner': auth.hasMarketingBanner() || auth.hasLogoBanner(),
          'has-login-banner': hasLoginBanner,
        })}
      >
        <img src={emptyIcon} alt="empty icon" />
        <div className="description">
          {t`The return status of a follow-on study is shown in its Holter study. Click “View Now” to be redirected to the Holter study.`}
        </div>
        <CustomButton id="view-now-redirected-btn" type="outline" onClick={onClickViewNow}>
          {t`View Now`}
        </CustomButton>
      </div>
    );
  }

  const isIcorDevice = checkIcorDevice(data?.device);
  if (isIcorDevice) {
    return (
      <NotAvailableComponent
        containerClassName="return-status-tab"
        message={t`Return status are not available for studies running on the iCOR device.`}
        hasLoginBanner={hasLoginBanner}
      />
    );
  }

  return (
    <div
      className={classNames('return-status-tab', {
        'has-marketing-banner': auth.hasMarketingBanner() || auth.hasLogoBanner(),
        'has-login-banner': hasLoginBanner,
      })}
    >
      <div className="selection">
        <div className="selection__left">
          <LoadingSkeleton isLoading={!isLoadedData} width={200}>
            <div className="selection__title">{t`Return status`}</div>
          </LoadingSkeleton>
          <div className="selection__body">
            <LoadingSkeleton isLoading={!isLoadedData}>
              <ReturnStatusCheckbox data={data} updateStudyDetail={updateStudyDetail} />
            </LoadingSkeleton>
          </div>
        </div>
        <div className="selection__right">
          <LoadingSkeleton isLoading={!isLoadedData} width={100}>
            <div className="selection__title">{t`Notes`}</div>
          </LoadingSkeleton>
          <div className="selection__body">
            <LoadingSkeleton isLoading={!isLoadedData}>
              <ReturnStatusNotes data={data} updateStudyDetail={updateStudyDetail} />
            </LoadingSkeleton>
          </div>
        </div>
      </div>
      <div className="tracking-infor">
        <div className="tracking-infor__header">
          <LoadingSkeleton height={32} isLoading={!isLoadedData}>
            <div className="text">{t`Return shipping information`}</div>
            {!isEmptyTrackingInfo && isLoadedData && createReturnShippingButton}
          </LoadingSkeleton>
        </div>
        <div className="tracking-infor__body">
          <LoadingSkeleton isLoading={!isLoadedData}>{renderTrackingInfor()}</LoadingSkeleton>
        </div>
      </div>
    </div>
  );
};

export default ReturnStatusTab;
